import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Truck, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useShop, useI18n } from "@/context/Shop";
import { media } from "@/lib/api";
import { formatALL } from "@/lib/format";
import { productPath } from "@/lib/nav";
import { Price, PriceInline } from "@/components/store/Price";

export function BagLines({ compact = false }) {
  const { cart, updateQty, removeLine } = useShop();
  const { t, tp } = useI18n();
  return (
    <ul className="divide-y" data-testid="bag-lines">
      {cart.items.map((l) => (
        <li key={l.id} className="py-5 flex gap-4" data-testid="bag-line">
          <Link to={l.product?.slug ? productPath(l.product.slug) : "#"} className={`shrink-0 bg-muted overflow-hidden ${compact ? "w-20 h-[106px]" : "w-24 h-32"}`}>
            {l.product?.primary_image && <img src={media(l.product.primary_image, 300)} alt="" className="w-full h-full object-cover" />}
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.14em] font-semibold leading-tight">{l.product?.brand_name}</p>
                <p className="text-[13px] text-foreground/80 truncate mt-1">{tp(l.product, "name")}</p>
                {l.size && <p className="text-xs text-muted-foreground mt-1">{l.color} · {t("size")} {l.size}</p>}
              </div>
              <button aria-label={t("remove")} onClick={() => removeLine(l.id)} className="p-1 h-7 hover:bg-secondary" data-testid="bag-drawer-remove-item-button"><X className="h-4 w-4" /></button>
            </div>
            {l.messages?.map((m) => <p key={m} className={`text-xs mt-2 ${l.status === "ok" ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--sale))]"}`} data-testid="bag-line-message">{m}</p>)}
            {l.status !== "unavailable" && (
              <div className="flex items-center justify-between mt-3">
                <div className="inline-flex items-center border">
                  <button aria-label="−" className="p-2 disabled:opacity-30" disabled={l.quantity <= 1} onClick={() => updateQty(l.id, l.quantity - 1)} data-testid="bag-line-decrease-button"><Minus className="h-3.5 w-3.5" /></button>
                  <span className="w-8 text-center text-sm tabular" data-testid="bag-line-quantity">{l.quantity}</span>
                  <button aria-label="+" className="p-2 disabled:opacity-30" disabled={l.quantity >= Math.min(10, l.available ?? 10)} onClick={() => updateQty(l.id, l.quantity + 1)} data-testid="bag-line-increase-button"><Plus className="h-3.5 w-3.5" /></button>
                </div>
                <PriceInline value={l.line_total || l.unit_price} className="text-sm" testid="bag-line-total" />
              </div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function FreeDeliveryBar() {
  const { cart, config } = useShop();
  const { t } = useI18n();
  const threshold = Number(config?.free_delivery_threshold || 0);
  if (!threshold || cart.item_count === 0) return null;
  const sub = Number(cart.subtotal || 0);
  const pct = Math.min(100, Math.round((sub / threshold) * 100));
  const left = Math.max(0, threshold - sub);
  return (
    <div className="space-y-2" data-testid="free-delivery-bar">
      <p className="text-xs flex items-center gap-2 text-foreground/80"><Truck className="h-3.5 w-3.5" strokeWidth={1.6} />{left > 0 ? t("add_more_for_free", { x: formatALL(left) }) : t("free_delivery_unlocked")}</p>
      <div className="h-1 bg-muted"><div className={`h-full ${left > 0 ? "bg-foreground" : "bg-[hsl(var(--success))]"}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export function BagSummary() {
  const { cart, config } = useShop();
  const { t } = useI18n();
  const navigate = useNavigate();
  const blocked = cart.item_count === 0 || cart.issues?.some((i) => i.code !== "PRICE_CHANGED");
  return (
    <div className="space-y-3" data-testid="bag-summary">
      <FreeDeliveryBar />
      <div className="flex justify-between items-start text-sm pt-2"><span>{t("subtotal")}</span><Price value={cart.subtotal} align="right" size="md" testid="bag-subtotal" /></div>
      <div className="flex justify-between text-sm text-muted-foreground"><span>{t("delivery")}</span><span>{t("delivery_calc")}</span></div>
      <div className="flex justify-between items-start pt-3 border-t"><span className="text-sm font-semibold">{t("estimated_total")}</span><Price value={cart.subtotal} align="right" size="lg" testid="bag-total" /></div>
      <button className="btn-ink w-full mt-2" disabled={!config?.checkout_enabled || blocked} onClick={() => navigate("/checkout")} data-testid="bag-checkout-button">{t("checkout")}</button>
      {!config?.checkout_enabled && <p className="text-xs text-muted-foreground leading-relaxed" data-testid="checkout-unavailable-note">{t("checkout_unavailable")}</p>}
      <p className="text-[11px] text-muted-foreground">{t("checkout_in_all")}</p>
    </div>
  );
}

export function CartDrawer() {
  const { bagOpen, setBagOpen, cart } = useShop();
  const { t } = useI18n();
  return (
    <Sheet open={bagOpen} onOpenChange={setBagOpen}>
      <SheetContent side="right" className="w-full sm:max-w-[440px] bg-background p-0 flex flex-col rounded-none" data-testid="bag-drawer">
        <SheetHeader className="px-6 py-5 border-b">
          <SheetTitle className="font-display text-2xl font-medium text-left">{t("your_bag")} {cart.item_count > 0 && <span className="text-base text-muted-foreground font-sans">({cart.item_count})</span>}</SheetTitle>
        </SheetHeader>
        {cart.items.length === 0 ? (
          <div className="flex-1 grid place-items-center px-8 text-center">
            <div>
              <ShoppingBag className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.2} />
              <p className="font-display text-2xl mt-4">{t("bag_empty")}</p>
              <p className="text-sm text-muted-foreground mt-2">{t("bag_empty_sub")}</p>
              <Link to="/new-arrivals" onClick={() => setBagOpen(false)} className="btn-ghost-lux mt-6" data-testid="bag-empty-shop-link">{t("shop_new")}</Link>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6"><BagLines compact /></div>
            <div className="border-t px-6 py-5 bg-background">
              <BagSummary />
              <Link to="/bag" onClick={() => setBagOpen(false)} className="block text-center text-[12px] uppercase tracking-[0.14em] font-semibold link-lux mt-4" data-testid="bag-drawer-view-bag-link">{t("view_bag")}</Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
